import OrderableTypes from "../Orders/OrderableTypes.js";
import TicketJourneyTypes from "../Tickets/TicketJourneyTypes.js";
import pluralize from "pluralize";
import {v4 as uuid} from "uuid";
import {getLabel as getJourneyTypeLabel} from "../Tickets/TicketJourneyTypes.js";
import {getLabel as DeliveryMethodLabel} from "../Orders/DeliveryMethods.js";
import SystemItemTypes from "../Orders/SystemItemTypes.js";
import GiftCardInstanceService from "../Orders/GiftCards/GiftCardInstanceService.js";
import * as Localisation from "../Utils/Localisation.js";

/**
 * Checkout basket item utility methods
 *
 * Enables interaction with objects representing HOPS checkout basket items.
 *
 * Most methods expect to work with a checkout basket item object created 
 * by the `construct()` method. Ideally we'd have a proper basket item 
 * class to create instances of to get these objects but this approach 
 * using plain objects and static methods is currently favoured as it 
 * simplifies persistence and hydration within clients.
 */

class CheckoutBasketItem {

	/**
	 * Get an item's UI label.
	 *
	 * This is how it will be labelled in the UI (e.g. in the checkout).
	 *
	 * @param {Object} item
	 * @return {String}
	 */
	static getUiLabel(item) {
		switch (item?.OrderableType) {
			case OrderableTypes.Membership:
				return `${item.Item?.Plan?.Type?.Name} Membership (${item.Item?.Plan?.Name})`;
			case OrderableTypes.Addon:
				return item.Item.Name;
			case OrderableTypes.TicketTravel:
				return `${item.Item.TicketOption.Class.Name} ${item.Item.TicketOption.Type.Name} ${getJourneyTypeLabel(item.Item.TicketOption.Journey)}`;
			case OrderableTypes.TicketSession:
				return item.Item.Session.Product.Name;
			case OrderableTypes.SeatReservation:
				return "Seat Reservation";
			case OrderableTypes.VoucherSale:
				return `${Localisation.formatCurrency(item.Price)} Voucher`;
			case OrderableTypes.Shares:
				return item.Item.ShareTypeLabel;
			case OrderableTypes.DeliveryMethod:
				return item.Item.Description || (item.Item?.Id && DeliveryMethodLabel(item.Item.Id)) || "Delivery";
			case OrderableTypes.System:
				if (item.Item.Id === SystemItemTypes.VoucherBalancingSale) return "Voucher Balance";
				else if (item.Item.Id === SystemItemTypes.UnknownPlaceholder) return "Unknown Placeholder";
				else if (item.Item.Id === SystemItemTypes.NoSale) return "No Sale";
				else return "Generic System Item";
			case OrderableTypes.GiftCardInventory:
				return `${item.Item.Name} Gift Card`;
			case OrderableTypes.GiftCardInstance:
				return `Redeem Gift Card ${GiftCardInstanceService.getGiftCardCodeDisplayString(item.Item?.Code)}`;
			default:
				return "Basket Item";
		}
	}


	/**
	 * Get an item's UI subtext.
	 *
	 * This is extra secondary information displayed alongside it in the UI.
	 *
	 * @param {Object} item
	 * @return {String|null}
	 */
	static getUiSubtext(item) {
		switch (item?.OrderableType) {
			case OrderableTypes.TicketTravel:
				return [item.Item.TicketOption?.TimingPointA, item.Item.TicketOption?.TimingPointB, item.Item.TicketOption?.TimingPointC].filter(tp => tp).map(tp => tp?.Name).join(" - ");
			case OrderableTypes.TicketSession:
				return `${item.Item.TicketType.Name} - ${this.getItemSessionDateTime(item)}`;
			case OrderableTypes.VoucherSale:
			case OrderableTypes.GiftCardInventory:
				return `Expires ${Localisation.formatDateTime(item.Item.Expiry)}`;
			case OrderableTypes.GiftCardInstance:
				return `Expires ${Localisation.formatDateTime(item.Item.ExpiryDate)}`;
			case OrderableTypes.Shares:
				return item.Item.ShareCompanyLabel;
			default:
				return null;
		}
	}

	/**
	 * Gets formatted session date and time, including start and end dates/times
	 * @param {object} item basket item
	 * @returns {string}
	 */
	static getItemSessionDateTime(item) {
		const session = item.Item.Session;

		const startDate = Localisation.formatDateTime(session.StartDate);
		const startTime = Localisation.formatDateTime(`${session.StartDate} ${session.StartTime}`, Localisation.DateTimeFormats.ShortTime);

		const endDate = Localisation.formatDateTime(session.EndDate);
		const endTime = Localisation.formatDateTime(`${session.EndDate || session.StartDate} ${session.EndTime}`, Localisation.DateTimeFormats.ShortTime);

		if (session.EndDate) {
			const startDateTime = `${startDate}${session.StartTime ? ` (${startTime})` : ""}`;
			const endDateTime = `${endDate}${session.EndTime ? ` (${endTime})` : ""}`;
			return `${startDateTime} - ${endDateTime}`; // e.g 2023-05-27 (16:30) - 2023-05-28 (17:30)
		}
		else {
			const st = session.StartTime ? startTime : "";
			const et = session.EndTime ? `- ${endTime}` : "";
			return `${startDate}${(st || et) ? ` (${(`${st} ${et}`).trim()})` : ""}`; // e.g 2023-05-27 (16:30 - 17:30)
		}
	}

	/**
	 * Get an item's UI details object.
	 *
	 * These are key-value pairs of extra tertiary information to display
	 * alongside the item in the UI generally within collapsible panels.
	 *
	 * @param {Object} item
	 * @return {Array} Items: Objects with `label`/value` strings
	 */
	static getUiDetails(item) {
		switch (item?.OrderableType) {
			case OrderableTypes.TicketTravel:
				return [
					{
						label: "Date/Time",
						value: `${Localisation.formatDateTime(item.Item.Date)}${(item.Item.Journeys?.[0] ? ` - ${item.Item.Journeys[0].Departure?.DepartureTime ? item.Item.Journeys[0].Departure?.DepartureTime?.substring(0, 5) : "Open"}` : "")}`
					},
					{
						label: "Fare Class",
						value: item.Item.TicketOption.Fares.find(f => (f.Id === item.Item.FareId))?.Type?.Name
					},
					{
						label: "Journey Type",
						value: getJourneyTypeLabel(item.Item.TicketOption.Journey)
					},
					{
						label: "Valid For",
						value: `${item.Item.TicketOption.Type.People} ${pluralize("Person", item.Item.TicketOption.Type.People)} ${((item.Quantity > 1) ? "(per item)" : "")}`.trim()
					}
				];
			case OrderableTypes.TicketSession:
				return [
					{
						label: "Fare Class",
						value: item.Item.FareType.Name
					},
					{
						label: "Valid For",
						value: `${item.Item.TicketType.People} ${pluralize("Person", item.Item.TicketType.People)} ${((item.Quantity > 1) ? "(per item)" : "")}`.trim()
					}
				];
			case OrderableTypes.Shares:
				return [
					{
						label: "Shareholder Type",
						value: item.Item.HoldingTypeLabel
					}
				];
			default:
				return [];
		}
	}


	/**
	 * Get an item's receipt text.
	 *
	 * It's expected that the other items in the basket will be supplied, 
	 * to e.g. allow accurate association of item relations with each other.
	 *
	 * @param {Object} item
	 * @param {Array<Object>} optional basketItems Other items in the basket
	 * @return {String|null} `null` = don't show on receipt
	 */
	static getReceiptText(item, basketItems=[]) {

		const currentDate = Localisation.formatDateTime(new Date());

		/**
		 * Travel ticket
		 */
		if (item.OrderableType === OrderableTypes.TicketTravel) {

			const to = item.Item.TicketOption;

			/**
			 * Date
			 */
			const date = Localisation.formatDateTime(item.Item.Date);

			/**
			 * Fare
			 */
			const fare = to.Fares.find(f => (f.Id === item.Item.FareId));

			/**
			 * Journey type name
			 */
			const journey = getJourneyTypeLabel(to.Journey);

			/**
			 * Timing points text
			 */
			const tps = [];

			/**
			 * Iterate the journeys and add their details
			 */
			item.Item.Journeys?.forEach(journey => {

				const dept = journey.Departure;
				const arrv = journey.Arrival;

				tps.push(`Dep ${dept?.DepartureTime?.substring(0, 5)} ${dept?.TimingPoint?.NameShort}${(dept?.RequestStop ? " [Request]" : "")}`);
				if (arrv) tps.push(`Arr ${arrv?.ArrivalTime?.substring(0, 5)} ${arrv?.TimingPoint?.NameShort}${(arrv?.RequestStop ? " [Request]" : "")}`);

				const seats = basketItems.filter(i => {
					return ((i.OrderableType === OrderableTypes.SeatReservation) &&
							(i.RelatedItemUuid === item.Uuid) &&
							(i.Item.DepartureTrainSchedule === dept.Id) &&
							(i.Item.ArrivalTrainSchedule === arrv.Id));
				});

				if (seats.length) {
					tps.push(`${pluralize("Seat", seats.length)}: ${seats.map(s => `${s.Item?.TrainAsset?.Letter}${(s.Item?.Space?.Label || s.Item?.Space?.Id)}`).join(", ")}`);
				}

			});

			/**
			 * Ranger journey = no trains available
			 */
			if (item.Item.TicketOption.Journey === TicketJourneyTypes.Ranger) {
				tps.push(`Dep: ${item.Item.TicketOption?.TimingPointA?.NameShort}`);
			}

			/**
			 * Seat count
			 */
			const seatCount = (item.Item.TicketOption.Type.People * item.Quantity);

			return [
				`Travel Ticket`,
				(!fare?.Type.Normal && fare?.Type.Name),
				(!to.Class.Normal && to.Class.Name),
				`${to.Type.Name} ${journey} ${((item.Quantity > 1) ? `"(x${item.Quantity})"` : "")}`.trim(),
				((seatCount > 1) && `${seatCount} Seats`),
				`Valid for ${item.Item.TicketOption.Type.People} ${pluralize("Person", item.Item.TicketOption.Type.People)} ${((item.Quantity > 1) ? "(per item)" : "")}`.trim(),
				((date !== currentDate) && `Date: ${date}`),
				...tps
			].filter(i => i).join("\n");

		}

		/**
		 * Ticket session
		 */
		else if (item.OrderableType === OrderableTypes.TicketSession) {

			const session = item.Item.Session;
			const seatCount = (item.Item.TicketType.People * item.Quantity);

			const {StartDate, StartTime, EndDate, EndTime} = session;
			const StartDateDmy = Localisation.formatDateTime(StartDate);
			const EndDateDmy = (EndDate ? Localisation.formatDateTime(EndDate) : null);

			let datetime = StartDateDmy;

			if (StartTime) {
				datetime += `, ${StartTime.substr(0, 5)}`;
			}
			if (EndDate) {
				if (EndDateDmy !== StartDateDmy) {
					datetime += ` - ${EndDateDmy}`;
				}
			}
			if (EndTime) {
				datetime += ((EndDate && (EndDateDmy !== StartDateDmy)) ? ", " : " - ");
				datetime += EndTime.substr(0, 5);
			}

			const lines = [
				session.Product.Name,
				(!item.Item.FareType.Normal && item.Item.FareType.Name),
				item.Item.TicketType.Name,
				`Valid for ${item.Item.TicketType.People} ${pluralize("Person", item.Item.TicketType.People)} ${((item.Quantity > 1) ? "(per item)" : "")}`.trim(),
				`Date: ${datetime}`,
				((seatCount > 1) && `${seatCount} Seats`)
			];

			const seats = basketItems.filter(i => {
				return ((i.OrderableType === OrderableTypes.SeatReservation) &&
						(i.RelatedItemUuid === item.Uuid));
			});

			if (seats.length) {

				const seatsByTrain = {};
				const trains = {};

				for (const seat of seats) {

					const train = seat.Item.Train;
					const trainId = train.Id;

					seatsByTrain[trainId] ||= [];
					seatsByTrain[trainId].push(seat);
					trains[trainId] ||= train;

				}

				const trainIds = Object.keys(seatsByTrain);
				const hasMultipleTrains = (trainIds.length > 1);

				for (const trainId of trainIds) {
					lines.push(`${pluralize("Seat", seats.length)}${(hasMultipleTrains ? ` (Train ${trains[trainId]?.Headcode})` : "")}: ${seats.map(s => `${s.Item?.TrainAsset?.Letter}${(s.Item?.Space?.Label || s.Item?.Space?.Id)}`).join(", ")}`);
				}

			}

			return lines.filter(i => i).join("\n");

		}

		/**
		 * Seat reservation
		 *
		 * These aren't shown on receipts as standard items - they're 
		 * aggregated separately by each of the trains they refer to.
		 */
		else if (item.OrderableType === OrderableTypes.SeatReservation) {
			return null;
		}

		/**
		 * Membership
		 * This is OK for receipts, the recall API just has
		 * the final membership description as a string.
		 */
		else if (item.OrderableType === OrderableTypes.Membership) {
			return item?.Item?.Description;
		}

		/**
		 * System Item
		 */
		else if (item.OrderableType === OrderableTypes.System) {
			if (item.Item?.Id === SystemItemTypes.NoSale) {
				const lines = [`"*** NO SALE ***"`];
				if (item.Item?.Description) lines.push(item.Item.Description);
				return lines.filter(i => i).join("\n");
			}
			else return this.getUiLabel(item);
		}

		/**
		 * Display the standard label only
		 */
		else {
			return this.getUiLabel(item);
		}

	}


	/**
	 * Get the data to send to the basket/checkout APIs for an item.
	 *
	 * This should pertain to the item only (not its orderable type).
	 *
	 * @param {Object} item
	 * @return {mixed}
	 */
	static getBasketApiData(item) {
		switch (item?.OrderableType) {
			case OrderableTypes.Membership:
				return {
					...item.Item,
					Plan: item.Item?.Plan?.Id
				};
			case OrderableTypes.Addon:
				return {
					Id: item.Item.Id,
					RequiresAction: item.Item.RequiresAction,
					RequiresFulfilment: item.Item.RequiresFulfilment
				};
			case OrderableTypes.TicketTravel:
				return {
					Date: item.Item.Date,
					TicketOption: item.Item.TicketOption.Id,
					Fare: item.Item.FareId,
					Journeys: item.Item.Journeys?.map(j => j.Id)
				};
			case OrderableTypes.TicketSession:
				return {
					Session: item.Item.Session.Id,
					Fare: item.Item.FareId
				};
			case OrderableTypes.SeatReservation:
				return {
					TrainAsset: item.Item.TrainAsset.Id,
					Space: item.Item.Space.Id,
					DepartureTrainSchedule: item.Item.DepartureTrainSchedule,
					ArrivalTrainSchedule: item.Item.ArrivalTrainSchedule
				};
			case OrderableTypes.VoucherSale:
				return {
					Id: item.Item.Id
				};
			case OrderableTypes.Shares:
				return {
					ShareType: item.Item.ShareType,
					HoldingType: item.Item.HoldingType,
					HoldingTypeText: item.Item.HoldingTypeText,
					HolderDob: item.Item.HolderDob,
					AlreadyShareholder: item.Item.AlreadyShareholder
				};
			case OrderableTypes.GiftCardInventory:
				return {
					Id: item.Item.Id
				};
			default:
				return item.Item;
		}
	}


	/**
	 * Get the train journeys associated with an item.
	 *
	 * @param {Object} item
	 * @return {Array<Object>} `Train`/`DepartureTrainSchedule`/`ArrivalTrainSchedule` objects
	 */
	static getJourneys(item) {
		switch (item?.OrderableType) {
			case OrderableTypes.TicketTravel:
				return item.Item.Journeys.map(journey => {
					return {
						Train: journey.Train,
						DepartureTrainSchedule: journey.Departure,
						ArrivalTrainSchedule: journey.Arrival
					};
				});
			case OrderableTypes.TicketSession:
				return item.Item.Session.Trains.map(train => {
					return {
						Train: train,
						DepartureTrainSchedule: null,
						ArrivalTrainSchedule: null
					};
				});
			default:
				return [];
		}
	}


	/**
	 * Get the total price of an item.
	 *
	 * @param {Object} item
	 * @return {Integer} Pence (inc. VAT)
	 */
	static getTotalPrice(item) {
		return (item.Price * item.Quantity);
	}


	/**
	 * Get the maximum per-order quantity of an item.
	 *
	 * @param {Object} item
	 * @return {Integer|null}
	 */
	static getMaxQuantity(item) {
		switch (item?.OrderableType) {
			case OrderableTypes.Membership:
				return 1;
			case OrderableTypes.Addon:
				return item.Item.MaxQuantity;
			case OrderableTypes.VoucherSale:
				return item.Item.MaxQuantity;
			default:
				return null;
		}
	}


	/**
	 * Get an item's seat reservation mode ID.
	 * 
	 * @param {Object} item
	 * @return {Integer|undefined}
	 */
	static getSeatReservationMode(item) {
		return (
			item.Item?.TicketOption?.SeatReservationMode ||
			item.Item?.Session?.SeatReservationMode
		);
	}


	/**
	 * Get the number of people targeted by an item.
	 *
	 * @param {Object} item
	 * @return {Integer}
	 */
	static getTargetPeopleCount(item) {
		switch (item?.OrderableType) {
			case OrderableTypes.TicketTravel:
				return item.Item.TicketOption.Type.People;
			case OrderableTypes.TicketSession:
				return item.Item.TicketType.People;
			case OrderableTypes.Membership:
				return item.Item.Plan.Type.MaxIndividuals;
			default:
				return 1;
		}
	}


	/**
	 * Get whether an item is Gift Aid-able.
	 *
	 * @param {Object} item
	 * @return {Boolean}
	 */
	static isGiftAidable(item) {
		switch (item?.OrderableType) {
			case OrderableTypes.Membership:
				return (item.Item?.Plan?.GiftAidProportion > 0);
			case OrderableTypes.Addon:
				return item.Item.GiftAid;
			default:
				return false;
		}
	}


	/**
	 * Get whether an item is an "internal" item (not shown in the UI).
	 *
	 * @param {Object} item
	 * @return {Boolean}
	 */
	static isInternal(item) {
		return (item?.OrderableType === OrderableTypes.SeatReservation);
	}


	/**
	 * Construct a persistable basket item object to represent a basket item.
	 *
	 * @param {String|undefined} options.Uuid A new UUID is assigned when falsey
	 * @param {String|undefined} options.DeliveryMethodItemUuid Delivery Method item UUID
	 * @param {String|undefined} options.RelatedItemUuid Related item UUID
	 * @param {Integer} options.OrderableType Item's orderable type ID
	 * @param {mixed} options.Item Data identifying this item within its orderable type
	 * @param {mixed} options.ClientItem Data identifying this item when offline within its orderable type
	 * @param {Integer} options.Price Price customer is paying inc. VAT
	 * @param {Integer} options.Quantity Quantity customer is buying
	 * @param {Integer} options.VatProportion VAT Proportion
	 * @param {Integer} options.VatRate VAT Rate
	 * @param {Integer|null} options.StationeryTemplateId Item's order stationery template ID
	 * @return {Object} Compatible as parameter to other methods in this class
	 */
	static construct({
		Uuid,
		DeliveryMethodItemUuid,
		RelatedItemUuid,
		OrderableType,
		Item,
		ClientItem,
		Price,
		Quantity,
		VatProportion,
		VatRate,
		StationeryTemplateId
	}) {

		if (!(Price >= 0)) {
			throw new Error("Price must be >= 0.");
		}
		else if (!((VatProportion >= 0) && (VatProportion <= 100))) {
			throw new Error("VatProportion must be >= 0.");
		}
		else if (!((VatRate >= 0) && (VatRate <= 100))) {
			throw new Error("VatRate must be >= 0.");
		}

		return {
			Uuid: (Uuid || uuid()),
			DeliveryMethodItemUuid: (DeliveryMethodItemUuid || null),
			RelatedItemUuid: (RelatedItemUuid || null),
			Item,
			ClientItem,
			OrderableType,
			Price,
			PriceListed: Price, // Price, as listed on the Orderable (generically, independent of Item)
			Quantity,
			VatProportion,
			VatRate,
			StationeryTemplateId
		};

	}

}

export default CheckoutBasketItem;
